// HeaderPersonaChip.jsx
// FILE VERSION: HEADERPERSONACHIP-v2.0
// CHARLIE VERSION: v1.7.42
//
// #2047 -- real revival of HeaderPersonaChip, which existed as its own
// header-level dropdown in App.js until #1805 (Goose, 2026-08-24) folded
// it into UserMenu's "Switch Persona" section. Stinger direct 2026-09-07:
// persona switching needs to be a compact, obvious header-level control,
// not buried inside the account dropdown.
//
// Deliberately a SWITCHER only, not a manager. UserMenu.jsx's own Switch
// Persona section (with drag-to-reorder, hide/show, and quick-pick
// curation -- real, tested, substantial UI) is left untouched rather than
// torn apart to avoid this component -- that curation/management job
// belongs in the account area, not crammed into a compact header chip.
// Real, deliberate scope split: fast-switch here, manage there.
//
// quickPersonaList logic is intentionally duplicated (not extracted into
// a shared hook) from UserMenu.jsx -- it's four lines of pure, stable
// computation, and threading a new shared hook through both files for
// this alone would be more real risk than the duplication itself. If it
// ever needs a third real consumer, extract then.
import React, { useState, useRef, useEffect } from 'react';
import { readDefaultPersona, saveDefaultPersona } from '../lib/personaPreference';

export default function HeaderPersonaChip({ partners, activePartner, onSwitchPartner, userId }) {
  const [open, setOpen] = useState(false);
  const [defaultId, setDefaultId] = useState(() => readDefaultPersona(userId));
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => { setDefaultId(readDefaultPersona(userId)); }, [userId]);

  if (!partners || partners.length === 0) return null;

  const hasCuratedQuickPicks = partners.some((p) => p.quick_picker === true);
  const quickPersonaList = hasCuratedQuickPicks
    ? partners.filter((p) => p.quick_picker === true || p.id === activePartner?.id)
    : partners;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Switch persona"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, fontWeight: 700, color: '#8b9ab0',
          background: 'transparent', cursor: 'pointer',
          padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gov-slate-200)',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gov-text-primary)'; e.currentTarget.style.borderColor = 'var(--gov-text-primary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#8b9ab0'; e.currentTarget.style.borderColor = 'var(--gov-slate-200)'; }}
      >
        <span style={{
          width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
          background: activePartner ? `linear-gradient(135deg, ${activePartner.brand_color || '#0077cc'}, ${activePartner.secondary_color || activePartner.brand_color || '#0077cc'})` : 'var(--gov-slate-300)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700,
        }}>
          {(activePartner?.name || '?')[0]}
        </span>
        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{activePartner?.name || 'Select persona'}</span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: 200,
          background: '#ffffff', border: '1px solid var(--gov-slate-200)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15,23,42,0.12)', padding: 6, zIndex: 300,
          maxHeight: 360, overflowY: 'auto',
        }}>
          {quickPersonaList.map((p) => {
            const isActive = p.id === activePartner?.id;
            const isDefault = p.id === defaultId;
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4, paddingRight: 6, background: isActive ? 'var(--gov-slate-100)' : 'transparent', borderRadius: 6 }}>
                <button
                  onClick={() => { onSwitchPartner && onSwitchPartner(p); setOpen(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, padding: '7px 10px', border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500, color: 'var(--gov-text-primary)', textAlign: 'left' }}
                >
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: `linear-gradient(135deg, ${p.brand_color || '#0077cc'}, ${p.secondary_color || p.brand_color || '#0077cc'})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{(p.name || '?')[0]}</span>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}{isDefault ? ' · Default' : ''}</span>
                </button>
                <button type="button" disabled={!userId} aria-label={isDefault ? `${p.name} is default on sign-in` : `Set ${p.name} as default on sign-in`} title={isDefault ? 'Default on sign-in' : 'Set as default on sign-in'} onClick={() => { if (saveDefaultPersona(userId, p.id)) setDefaultId(p.id); }} style={{ border: 0, background: 'transparent', color: isDefault ? 'var(--gov-success)' : 'var(--gov-text-sub)', cursor: userId ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', fontSize: 11, padding: '5px 3px' }}>{isDefault ? '✓ Default' : 'Set as default'}</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
